import React, { useState, useCallback, useEffect, useRef } from "react";
import { Input, Alert, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { Tag } from "@/pages/datahub/DataHubFilesPage";
import { logger } from "@/telemetry";

interface TagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
  showAddNewTag?: boolean;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  disabled = false,
  showAddNewTag = false,
  className = "",
  label = "Tags",
}) => {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loadingTags, setLoadingTags] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const { platformClient } = useWebuildAPIClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  // Fetch tags from platform client
  const fetchTags = useCallback(async () => {
    if (!platformClient) return;
    try {
      setLoadingTags(true);
      setError(null);
      const response = await platformClient.tags.list({});

      if (response && Array.isArray(response)) {
        setAllTags(response as Tag[]);
      }
    } catch (error) {
      logger.error("Failed to fetch tags", error);
      setError("Failed to load tags");
    } finally {
      setLoadingTags(false);
    }
  }, [platformClient]);

  useEffect(() => {
    if (platformClient) {
      fetchTags();
    }
  }, [platformClient, fetchTags]);

  // Add new tag by name
  const addNewTagByName = async (tagName: string) => {
    const trimmedTag = tagName.trim();

    if (
      !trimmedTag ||
      allTags.some((t) => t.name.toLowerCase() === trimmedTag.toLowerCase()) ||
      selectedTags.some((t) => t.toLowerCase() === trimmedTag.toLowerCase())
    ) {
      setError("Tag already exists");

      return;
    }
    try {
      setError(null);
      if (platformClient) {
        await platformClient.tags.create({
          createTagRequest: { name: trimmedTag },
        });
      }
      await fetchTags();
      onTagsChange([...selectedTags, trimmedTag]);
      setInputValue("");
      setShowDropdown(false);
    } catch (error) {
      logger.error("Failed to create tag", error);
      setError(error instanceof Error ? error.message : "Failed to create tag");
    }
  };

  // Remove tag from selected
  const handleRemoveTag = (tagName: string) => {
    onTagsChange(selectedTags.filter((t) => t !== tagName));
  };

  // Filter available tags (exclude selected, filter by input)
  const filteredTags = allTags
    .map((t) => t.name)
    .filter(
      (name) =>
        !selectedTags.some((t) => t.toLowerCase() === name.toLowerCase()) &&
        (!inputValue || name.toLowerCase().includes(inputValue.toLowerCase())),
    )
    .sort((a, b) => a.localeCompare(b));

  // Check if inputValue is a new tag
  const isNewTag =
    inputValue.trim() &&
    !allTags.some((t) => t.name.toLowerCase() === inputValue.trim().toLowerCase()) &&
    !selectedTags.some((t) => t.toLowerCase() === inputValue.trim().toLowerCase());

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setError(null);
    setShowDropdown(true);
  };

  // Handle tag select from dropdown
  const handleTagSelect = (tagName: string) => {
    if (disabled) return;
    onTagsChange([...selectedTags, tagName]);
    setInputValue("");
    setShowDropdown(false);
  };

  // Handle add new tag
  const handleAddNewTag = async () => {
    if (isNewTag) {
      await addNewTagByName(inputValue.trim());
    }
  };

  // Hide dropdown on blur (with delay for click)
  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 150);
  };

  // Show dropdown on focus
  const handleFocus = () => {
    setShowDropdown(true);
  };

  // Handle delete tag from dropdown
  const handleDeleteTag = async (tagName: string) => {
    if (!platformClient) return;
    try {
      setLoadingTags(true);
      setError(null);
      const tagObj = allTags.find((t) => t.name === tagName);

      if (tagObj) {
        const tagId = (tagObj as any).id;

        if (tagId) {
          await platformClient.tags.delete({ tagId });
          await fetchTags();
        }
      }
    } catch {
      setError("Failed to delete tag");
    } finally {
      setLoadingTags(false);
    }
  };

  return (
    <div className={`space-y-2 ${className}`} data-id="tag-selector">
      <div className="flex flex-col gap-2 justify-start">
        <p className="text-sm font-medium text-brand-primary dark:!text-white">{label}</p>
        <div className="relative w-64">
          <Input
            ref={inputRef}
            aria-label="Tag search/add input"
            autoComplete="off"
            classNames={{ input: "focus:outline-none" }}
            data-id="tag-search-input"
            isDisabled={disabled}
            placeholder={loadingTags ? t("Loading tags...") : t("Search or add tags")}
            size="sm"
            value={inputValue}
            onBlur={handleBlur}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (filteredTags.length > 0) {
                  handleTagSelect(filteredTags[0]);
                } else if (inputValue.trim() && showAddNewTag && isNewTag) {
                  handleAddNewTag();
                }
              }
            }}
          />
          {showDropdown && (filteredTags.length > 0 || isNewTag) && (
            <div className="absolute left-0 right-0 bottom-full mb-1 bg-content1 border border-gray-200 rounded shadow-lg z-20 max-h-28 overflow-y-auto">
              {filteredTags.map((tag, index) => (
                <div
                  key={`${tag}-${index}-input`}
                  className="px-3 py-2 cursor-pointer hover:bg-brand-primary/10 text-sm flex items-center justify-between"
                  onMouseDown={() => handleTagSelect(tag)}
                >
                  <span>{tag}</span>
                  <span title="Delete tag">
                    <Icon
                      className="ml-2 text-gray-400 hover:text-red-500 cursor-pointer"
                      height={16}
                      icon="mdi:delete"
                      width={16}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleDeleteTag(tag);
                      }}
                    />
                  </span>
                </div>
              ))}
              {isNewTag && showAddNewTag && (
                <div
                  className="px-3 py-2 cursor-pointer hover:bg-brand-primary/10 text-sm flex items-center text-brand-primary"
                  onMouseDown={handleAddNewTag}
                >
                  <Icon className="mr-1" icon="mdi:plus" /> Add tag: "{inputValue.trim()}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {error && (
        <Alert
          aria-label="Tag selector error"
          className="mb-2"
          color="danger"
          data-id="tag-selector-error"
        >
          {error}
        </Alert>
      )}
      <div className="flex flex-wrap gap-2">
        {selectedTags.map((tag, index) => (
          <Chip
            key={`${tag}-${index}`}
            className="bg-brand-primary text-white"
            endContent={
              <Icon
                className="cursor-pointer ml-1"
                height={14}
                icon="mdi:close"
                width={14}
                onClick={() => handleRemoveTag(tag)}
              />
            }
          >
            {tag}
          </Chip>
        ))}
      </div>
    </div>
  );
};
